import { Box, SvgIcon as MUISvgIcon, Typography } from "@mui/material"

import { Container, ResponsiveImage, WYSIWYG } from ".."
import type { FC, ResponsiveImageType } from ".."

type BlogAuthorOverviewType = {
  title: string
  subtitle: string
  description: string
  image: ResponsiveImageType
}

const BlogAuthorOverview: FC<BlogAuthorOverviewType> = ({
  title,
  subtitle,
  description,
  image,
}) => {
  return (
    <Box position="relative" sx={{ background: (theme) => theme.palette["tertiary.200"].main }}>
      <BackgroundIcon />
      <Container
        maxWidth={1600}
        position="relative"
        display="flex"
        justifyContent="space-between"
        flexDirection={{ xs: "column", md: "row" }}
        gap={{ xs: 0, md: 10 }}
        sx={{
          px: { xs: 5, lg: 10 },
        }}
      >
        <Box
          position={{ xs: "relative", md: "static" }}
          display="flex"
          alignSelf={{ xs: "center", md: "flex-start" }}
          sx={(theme) => ({
            marginTop: { xs: "-140px", md: "-100px", lg: "-150px" },
            width: { xs: "180px", lg: "300px" },
            height: { xs: "180px", lg: "300px" },
            borderRadius: theme.spacing(1.5),
            zIndex: 1,
          })}
        >
          <BlogAuthorImage image={image} />
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          mt={{ xs: 0, md: 30 }}
          mb={{ xs: 20, lg: 30 }}
          width={{ xs: "100%", md: "840px" }}
        >
          <BlogAuthorTitle title={title} />
          <BlogAuthorSubtitle subtitle={subtitle} />
          <BlogAuthorDescription description={description} />
        </Box>
      </Container>
    </Box>
  )
}

export default BlogAuthorOverview

/** A sub-component for the Blog author picture */
const BlogAuthorImage: FC<Pick<BlogAuthorOverviewType, "image">> = ({ image }) => {
  return (
    <Box
      sx={{
        background: "#F2EAD3",
        width: { xs: "180px", lg: "300px" },
        height: { xs: "180px", lg: "300px" },
        borderRadius: "500px",
      }}
    >
      <ResponsiveImage
        image={image}
        sx={{ borderRadius: "500px", boxShadow: "0px 20px 20px 0px rgba(0, 0, 0, 0.05)" }}
      />
    </Box>
  )
}

/** A sub-component for the Blog author title */
const BlogAuthorTitle: FC<Pick<BlogAuthorOverviewType, "title">> = ({ title }) => {
  return (
    <Box mt={{ xs: 15, md: 0 }}>
      <Typography variant="heading.2xl" component="h1">
        {title}
      </Typography>
    </Box>
  )
}

/** A sub-component for the Blog author title */
const BlogAuthorSubtitle: FC<Pick<BlogAuthorOverviewType, "subtitle">> = ({ subtitle }) => {
  return (
    <Box mt={{ xs: 3, md: 5 }} mb={{ xs: 10, md: 15 }}>
      <Typography variant="body.sm" component="h3">{subtitle}</Typography>
    </Box>
  )
}

/** A sub-component for the Blog author description */
const BlogAuthorDescription: FC<Pick<BlogAuthorOverviewType, "description">> = ({
  description,
}) => {
  return <WYSIWYG content={description} />
}

/** A sub-component for the animated svg in the bg  */
const BackgroundIcon = () => {
  return (
    <Box
      sx={(theme) => ({
        position: "absolute",
        top: 0,
        display: { xs: "none", md: "block" },
        overflow: "hidden",

        [theme.breakpoints.down(1600)]: {
          right: 920,
        },

        [theme.breakpoints.up(1600)]: {
          left: 0,
        },
      })}
    >
      <MUISvgIcon
        viewBox="0 0 663 418"
        sx={{
          width: 663,
          height: 418,
          fill: "none",
        }}
      >
        <path
          d="M-1.62173e-07 413.252C84.125 448.099 77.6391 226 198.5 226C385.897 226 347.5 413.252 503.935 375.244C652.944 339.04 506.583 -9.57436 662 1.2402"
          stroke="#F05B2A"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="0.1 10"
        />
      </MUISvgIcon>
    </Box>
  )
}
